import { useQueryClient } from '@tanstack/react-query'
import pluralize from 'pluralize'
import { useState } from 'react'
import { toast } from 'sonner'

import { ConnectTerminalTelematics } from '@/components/ConnectTerminalTelematics'
import { Button } from '@/components/ui/button'
import { listSentence } from '@/lib/formatters'
import {
  connectEld,
  type EldImportResult,
  fillApplicationErrorMessage,
  type FillApplicationState,
  importEldFleet,
  invalidateFillApplication,
  useFillApi,
} from '@/models/fillApplication'

/** What one pull brought in, or that the provider hasn't sent the fleet yet. */
export function eldImportMessage(result: EldImportResult): string {
  const provider = result.providerName || 'The ELD'
  const counts = [
    result.trucks > 0 ? pluralize('truck', result.trucks, true) : null,
    result.trailers > 0 ? pluralize('trailer', result.trailers, true) : null,
    result.drivers > 0 ? pluralize('driver', result.drivers, true) : null,
  ].filter((count): count is string => count !== null)
  if (counts.length === 0) return `${provider} hasn't sent the fleet yet. Import again in a minute.`
  return `Brought in ${listSentence(counts)} from ${provider}.`
}

interface EldButtonProps {
  orgId: string
  eld: FillApplicationState['eld']
}

/** Beside the Equipment heading: link the customer's ELD, or pull its fleet again once linked. */
export function EldButton({ orgId, eld }: EldButtonProps) {
  const api = useFillApi()
  const queryClient = useQueryClient()
  const [importing, setImporting] = useState(false)
  const { connected, providerName } = eld

  async function landed(result: EldImportResult) {
    toast.success(eldImportMessage(result))
    await invalidateFillApplication(queryClient, orgId)
  }

  async function connect(publicToken: string) {
    try {
      await landed(await connectEld(orgId, publicToken, api))
    } catch (error) {
      toast.error(fillApplicationErrorMessage(error))
    }
  }

  async function importAgain() {
    setImporting(true)
    try {
      await landed(await importEldFleet(orgId, api))
    } catch (error) {
      toast.error(fillApplicationErrorMessage(error))
    } finally {
      setImporting(false)
    }
  }

  if (connected) {
    return (
      <Button
        type="button"
        variant="outline"
        onClick={() => void importAgain()}
        loading={importing ? 'Importing…' : undefined}
      >
        Import from {providerName || 'the ELD'}
      </Button>
    )
  }
  // They sign in to their ELD provider, so the rep connects it with them on a call.
  return (
    <ConnectTerminalTelematics
      exchange={connect}
      externalId={orgId}
      handleSuccess={() => undefined}
      buttonProps={{ variant: 'outline', children: 'Connect ELD' }}
    />
  )
}
